// PR-INV-RATES MAY 11 — investor rates panel for one site contract.
// Lists active + historical investor-specific rates with an Add button.
import { useCallback, useEffect, useState } from 'react';
import { Plus, Loader2, Trash2, History } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AddInvestorRateSheet } from './AddInvestorRateSheet';

interface RateRow {
  id: string;
  investorId: string;
  investor: { id: string; code: string; fullNameEn: string | null };
  investorRateAtTarget: string | number;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
}

interface InvestorRatesPanelProps {
  siteContractId: string;
  defaultRateLabel?: string;
}

const fmtRate = (n: number | string) => 'SAR ' + Number(n).toFixed(2);
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString('en-GB') : '—');

export function InvestorRatesPanel({ siteContractId, defaultRateLabel }: InvestorRatesPanelProps) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const canEdit = isSuperAdmin || user?.role === 'admin' || user?.role === 'accountant';

  const [rates, setRates] = useState<RateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiClient.get<{ items: RateRow[] }>('/api/v1/site-contracts/' + siteContractId + '/investor-rates');
      setRates(r.data.items ?? []);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [siteContractId]);

  useEffect(() => { refresh(); }, [refresh]);

  const active = rates.filter((r) => r.effectiveTo == null);
  const historical = rates.filter((r) => r.effectiveTo != null);

  async function handleDelete(rateId: string) {
    if (!window.confirm('Hard delete this investor rate row? This is not undoable.')) return;
    try {
      await apiClient.delete('/api/v1/investor-rates/' + rateId);
      await refresh();
    } catch (e) { window.alert(extractErrorMessage(e)); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Investor-specific rates</div>
          {defaultRateLabel && (
            <div className="text-[11px] text-muted-foreground">Contract default: <span className="font-mono">{defaultRateLabel}</span></div>
          )}
        </div>
        {canEdit && (
          <Button variant="gold" size="sm" onClick={() => setAdding(true)} className="gap-1">
            <Plus className="h-4 w-4" /> Add investor rate
          </Button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading rates…</div>
      ) : (
        <>
          {/* Active rates */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border">
              Active ({active.length})
            </div>
            {active.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground text-center italic">No investor-specific rates set. Trips will use the contract default.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/20 text-muted-foreground"><tr>
                  <th className="text-left px-3 py-2 font-normal">Investor</th>
                  <th className="text-right px-3 py-2 font-normal">Rate</th>
                  <th className="text-left px-3 py-2 font-normal">Effective from</th>
                  <th className="text-left px-3 py-2 font-normal">Status</th>
                  {isSuperAdmin && <th className="text-end px-3 py-2 font-normal w-12" />}
                </tr></thead>
                <tbody>
                  {active.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="font-mono text-xs text-muted-foreground">{r.investor.code}</div>
                        <div>{r.investor.fullNameEn ?? '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{fmtRate(r.investorRateAtTarget)}</td>
                      <td className="px-3 py-2">{fmtDate(r.effectiveFrom)}</td>
                      <td className="px-3 py-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">ACTIVE</span>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-3 py-2 text-end">
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} className="text-rose-700 dark:text-rose-400 hover:bg-rose-500/10" title="Hard delete (super_admin only)">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Historical */}
          {historical.length > 0 && (
            <div className="rounded-lg border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => setShowHistory((s) => !s)}
                className="w-full px-3 py-2 text-xs uppercase tracking-wider text-muted-foreground bg-muted/30 border-b border-border inline-flex items-center gap-2 hover:bg-muted/40"
              >
                <History className="h-3 w-3" /> History ({historical.length}) — {showHistory ? 'hide' : 'show'}
              </button>
              {showHistory && (
                <table className="w-full text-sm">
                  <thead className="bg-muted/10 text-muted-foreground"><tr>
                    <th className="text-left px-3 py-2 font-normal">Investor</th>
                    <th className="text-right px-3 py-2 font-normal">Rate</th>
                    <th className="text-left px-3 py-2 font-normal">From</th>
                    <th className="text-left px-3 py-2 font-normal">To</th>
                    <th className="text-left px-3 py-2 font-normal">Status</th>
                    {isSuperAdmin && <th className="text-end px-3 py-2 font-normal w-12" />}
                  </tr></thead>
                  <tbody>
                    {historical.map((r) => (
                      <tr key={r.id} className="border-t border-border text-muted-foreground">
                        <td className="px-3 py-2">
                          <div className="font-mono text-xs">{r.investor.code}</div>
                          <div>{r.investor.fullNameEn ?? '—'}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{fmtRate(r.investorRateAtTarget)}</td>
                        <td className="px-3 py-2">{fmtDate(r.effectiveFrom)}</td>
                        <td className="px-3 py-2">{fmtDate(r.effectiveTo)}</td>
                        <td className="px-3 py-2">
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground border border-border">EXPIRED</span>
                        </td>
                        {isSuperAdmin && (
                          <td className="px-3 py-2 text-end">
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} className="text-rose-700 dark:text-rose-400 hover:bg-rose-500/10" title="Hard delete (super_admin only)">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      <AddInvestorRateSheet
        open={adding}
        onOpenChange={setAdding}
        contractId={siteContractId}
        activeRates={active.map((r) => ({ investorId: r.investorId, investorRateAtTarget: r.investorRateAtTarget }))}
        onSaved={refresh}
      />
    </div>
  );
}
